import axios from 'axios';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  AdminPanelSettingsRounded,
  RefreshRounded,
  VerifiedUserRounded,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import {
  bootstrapPlatformOwner,
  getPlatformOwnerBootstrapStatus,
} from '../services/platformAdminService';


function getErrorMessage(
  error: unknown,
  fallback: string,
): string {
  if (axios.isAxiosError(error)) {
    const detail = error.response?.data?.detail;

    if (
      typeof detail === 'string'
      && detail.trim()
    ) {
      return detail;
    }
  }

  return fallback;
}


export default function PlatformAdminBootstrapPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const statusQuery = useQuery({
    queryKey: ['platform-owner-bootstrap-status'],
    queryFn: getPlatformOwnerBootstrapStatus,
    retry: false,
    staleTime: 0,
  });

  const bootstrapMutation = useMutation({
    mutationFn: bootstrapPlatformOwner,
    onSuccess: async (context) => {
      queryClient.setQueryData(
        ['platform-admin-context'],
        context,
      );

      await queryClient.invalidateQueries({
        queryKey: ['platform-owner-bootstrap-status'],
      });

      navigate(
        '/platform-admin',
        {
          replace: true,
        },
      );
    },
  });

  if (statusQuery.isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#020617',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (statusQuery.isError) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: '#020617',
          py: 5,
        }}
      >
        <Container maxWidth="sm">
          <Alert
            severity="error"
            variant="outlined"
            action={
              <Button
                color="inherit"
                size="small"
                startIcon={<RefreshRounded />}
                onClick={() => {
                  void statusQuery.refetch();
                }}
              >
                Reintentar
              </Button>
            }
          >
            No se pudo comprobar el bootstrap de Platform Admin.
          </Alert>
        </Container>
      </Box>
    );
  }

  const bootstrapStatus = statusQuery.data;

  if (!bootstrapStatus) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: '#020617',
          py: 5,
        }}
      >
        <Container maxWidth="sm">
          <Alert
            severity="error"
            variant="outlined"
          >
            No se recibió un estado válido del bootstrap de Platform Admin.
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#020617',
        color: '#f8fafc',
        py: {
          xs: 3,
          md: 6,
        },
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={0}
          sx={{
            bgcolor: '#0f172a',
            border: '1px solid #334155',
            borderRadius: 4,
            p: {
              xs: 2.5,
              md: 4,
            },
          }}
        >
          <Stack spacing={2.5}>
            <Stack
              direction="row"
              spacing={1.25}
              alignItems="center"
            >
              <AdminPanelSettingsRounded
                sx={{
                  color: '#38bdf8',
                }}
              />

              <Box>
                <Typography
                  component="h1"
                  sx={{
                    fontSize: '1.4rem',
                    fontWeight: 950,
                  }}
                >
                  Platform Owner
                </Typography>

                <Typography
                  sx={{
                    color: '#94a3b8',
                    fontSize: '0.9rem',
                  }}
                >
                  Alta inicial del propietario global de PROA.
                </Typography>
              </Box>
            </Stack>

            {bootstrapStatus.current_user_is_owner ? (
              <Alert
                severity="success"
                variant="outlined"
              >
                Tu cuenta ya es owner global de PROA.
              </Alert>
            ) : null}

            {!bootstrapStatus.current_user_is_owner
            && bootstrapStatus.owner_exists ? (
              <Alert
                severity="info"
                variant="outlined"
              >
                El bootstrap inicial ya está cerrado porque existe un owner
                activo de plataforma.
              </Alert>
            ) : null}

            {!bootstrapStatus.current_user_is_owner
            && !bootstrapStatus.owner_exists
            && !bootstrapStatus.configured ? (
              <Alert
                severity="warning"
                variant="outlined"
              >
                El backend todavía no tiene configurado el usuario autorizado
                para realizar el bootstrap inicial.
              </Alert>
            ) : null}

            {!bootstrapStatus.current_user_is_owner
            && !bootstrapStatus.owner_exists
            && bootstrapStatus.configured
            && !bootstrapStatus.eligible ? (
              <Alert
                severity="warning"
                variant="outlined"
              >
                La cuenta autenticada no coincide con la cuenta autorizada para
                crear el primer Platform Owner.
              </Alert>
            ) : null}

            <Typography
              sx={{
                color: '#cbd5e1',
                fontSize: '0.9rem',
              }}
            >
              {bootstrapStatus.message}
            </Typography>

            {bootstrapMutation.isError ? (
              <Alert
                severity="error"
                variant="outlined"
              >
                {getErrorMessage(
                  bootstrapMutation.error,
                  'No se pudo completar el bootstrap del Platform Owner.',
                )}
              </Alert>
            ) : null}

            {bootstrapStatus.bootstrap_available ? (
              <Button
                variant="contained"
                size="large"
                startIcon={<VerifiedUserRounded />}
                disabled={bootstrapMutation.isPending}
                onClick={() => {
                  bootstrapMutation.mutate();
                }}
                sx={{
                  textTransform: 'none',
                  fontWeight: 900,
                  py: 1.25,
                }}
              >
                {bootstrapMutation.isPending
                  ? 'Activando Platform Owner...'
                  : 'Convertir esta cuenta en Platform Owner'}
              </Button>
            ) : null}

            {bootstrapStatus.current_user_is_owner ? (
              <Button
                variant="contained"
                onClick={() => {
                  navigate('/platform-admin');
                }}
                sx={{
                  textTransform: 'none',
                  fontWeight: 900,
                }}
              >
                Abrir Platform Admin
              </Button>
            ) : null}
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
}